import {
  NewOrderType,
  TBaseKeyValue,
  TBaseKeyword,
  TNewOrderCountry,
  TNewOrderForm,
  TViewsKeyValue,
  TViewsKeyword,
} from "@/entities/new-order";
import { TReviewsKeyword } from "@/entities/new-order/types/new-order-keywords.type";
import {
  getDatesListFromCountry,
  getKeysFromCountry,
} from "@/entities/new-order/utils";
import { CountryItem } from "@/features/new-order/country-item";
import { getDatesBetween } from "@/shared/helpers";
import { useEffect } from "react";
import { useFieldArray, useFormContext } from "react-hook-form";
import styles from "./countries-block.module.scss";
import { CountriesTotal } from "./countries-total";

export const CountriesBlock = () => {
  const { watch, setValue, control } = useFormContext<TNewOrderForm>();

  const { countriesList, dateStart, dateStop, type } = watch();

  const { fields, append, remove, update, replace } = useFieldArray({
    control,
    name: "countriesList",
  });

  useEffect(() => {
    //срабатывает первее чем очистка стейта
    if (!dateStart || !dateStop || !countriesList.length) {
      return;
    }
    const newDateArray = getDatesBetween(dateStart, dateStop);
    let isChanged = false;

    const newCountriesList: TNewOrderCountry[] = countriesList.map(
      (country) => {
        if (!("keywords" in country)) {
          return country;
        }

        const prevKeywordsObj = country.keywords;
        const prevDateArray = getDatesListFromCountry(country);
        const hasSameDates =
          prevDateArray.length === newDateArray.length &&
          newDateArray.every((date) => prevDateArray.includes(date));

        const isReviewType = type === NewOrderType.review;
        const isReviewKeywords = prevDateArray.some((date) =>
          Array.isArray((prevKeywordsObj as Record<string, unknown>)[date]),
        );

        if (isReviewType && !isReviewKeywords) {
          return country;
        }

        const hasTypeMismatch = !isReviewType && isReviewKeywords;

        if (hasSameDates && !hasTypeMismatch) {
          return country;
        }

        isChanged = true;

        const isViewsType = type === NewOrderType.install_with_views;

        if (isReviewType) {
          const reviewKeywords = prevKeywordsObj as TReviewsKeyword;
          const newReviewKeywords: TReviewsKeyword = {};

          newDateArray.forEach((newDate) => {
            newReviewKeywords[newDate] = reviewKeywords[newDate] ?? [];
          });

          return {
            ...country,
            keywords: newReviewKeywords,
          };
        }

        const regularKeywords = prevKeywordsObj as TBaseKeyword | TViewsKeyword;
        const newKeywordsObj: TBaseKeyword | TViewsKeyword = {};
        let countryKeys: string[] = [];

        if (Object.values(country.keywords).length && !isReviewKeywords) {
          countryKeys = getKeysFromCountry(country);
        }

        newDateArray.forEach((newDate) => {
          if (prevDateArray.find((prevDate) => prevDate === newDate)) {
            newKeywordsObj[newDate] = regularKeywords[newDate];
            return;
          }

          newKeywordsObj[newDate] = {};
          countryKeys.forEach((key) => {
            if (isViewsType) {
              (newKeywordsObj[newDate] as TViewsKeyValue)[key] = {
                installs: 0,
                views: 0,
                CR: 100,
              };
            } else {
              (newKeywordsObj[newDate] as TBaseKeyValue)[key] = 0;
            }
          });
        });

        return {
          ...country,
          keywords: newKeywordsObj,
        };
      },
    );

    if (isChanged) {
      replace(newCountriesList);
    }
  }, [dateStart, dateStop, type, countriesList]);

  return (
    <div className={styles.countries_block}>
      <ul className={styles.list}>
        {fields.map((field, index) => (
          <CountryItem
            countryIndex={index}
            key={field.id}
            remove={remove}
            update={update}
            append={append}
          />
        ))}
      </ul>
      <CountriesTotal append={append} />
    </div>
  );
};
